import { isFromBreakpoint } from '~/breakpoints';
import { classes, debounce } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

if (!customElements.get('product-gallery-zoom'))
  customElements.define(
    'product-gallery-zoom',
    class ProductGalleryZoom extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.on(
          'resize',
          window,
          debounce(() => {
            if (isFromBreakpoint('large')) return;

            if (this.targets.dialog) {
              this.targets.dialog.close();
            }
          })
        );

        if (this.targets.thumbnail)
          this.on('click', this.targets.thumbnail.all, ({ currentTarget }) =>
            this.setCurrentItem(currentTarget.dataset.id)
          );

        if (this.targets.item)
          this.targets.item.all.forEach((element) => {
            let offsetX, offsetY;

            const image = element.querySelector('img');

            this.on('click', element, (event) => {
              if (!element.classList.contains('zoomed-in')) {
                /**
                 * Zoom in on click.
                 */
                element.classList.add('zoomed-in');

                /**
                 * Calculate initial offset to center zoom on click.
                 */
                const rect = element.getBoundingClientRect();

                offsetX = event.clientX - rect.left;
                offsetY = event.clientY - rect.top;

                if (image) {
                  image.style.transformOrigin = `${offsetX}px ${offsetY}px`;
                  image.style.transform = 'scale(2)';
                }
              } else {
                this.resetItemZoom(element, image);
              }
            });

            this.on('mousemove', element, (event) => {
              if (element.classList.contains('zoomed-in')) {
                /**
                 * Move the image around based on the cursor position.
                 */
                const rect = element.getBoundingClientRect();
                const x = event.clientX - rect.left - offsetX;
                const y = event.clientY - rect.top - offsetY;

                image.style.transform = `scale(2) translate(${-x}px, ${-y}px)`;
              }
            });
          });
      }

      /**
       * Resets the item image zoom.
       * @param {HTMLElement} element - The container element.
       * @param {HTMLImageElement} image - The image element.
       */
      resetItemZoom(element, image = element.querySelector('img')) {
        /**
         * Reset zoom on second click.
         */
        element.classList.remove('zoomed-in');

        if (image) {
          image.style.transform = 'scale(1)';
        }
      }

      /**
       * Opens the dialog and sets the current item, if provided.
       * @param {String} id - The block identifier.
       */
      open(id) {
        if (this.targets.dialog) {
          this.targets.dialog.open();
        }

        if (id) {
          this.setCurrentItem(id, true);
        }
      }

      /**
       * Sets the currently active item.
       * @param {String} id - The block identifier.
       * @param {Boolean} scrollIntoView - Scrolls the active thumbnail into view.
       */
      setCurrentItem(id, scrollIntoView) {
        [
          ...((this.targets.item && this.targets.item.all) ?? []),
          ...((this.targets.thumbnail && this.targets.thumbnail.all) ?? []),
        ].forEach((element) => {
          const isActive = element.dataset.id === id;

          element.classList.toggle(classes.active, isActive);

          if (element.dataset.target === this.targets.item.dataset.target) {
            this.resetItemZoom(element);
          }

          if (scrollIntoView && element.dataset.target === this.targets.thumbnail.dataset.target && isActive) {
            element.scrollIntoView();
          }
        });
      }
    }
  );
